import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/common/Button';
import { StatusBadge } from '@/components/common/Badge';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { guardiansApi } from '@/services/api/endpoints/guardians';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { Guardian, GuardianStatus } from '@/types/domain';
import { orDash } from '@/utils/format';
import { GuardianFormModal, toPayload } from './GuardianForm';

/**
 * Guardians as people, not as fields on a student.
 *
 * The linked-children count is the column that justifies the design: one record
 * serving three siblings means a change of phone number reaches all three, and
 * the alternative — the same person typed into three student records — is three
 * numbers that quietly diverge.
 */
export function GuardiansPage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('lastName,asc');

  const [statusFilter, setStatusFilter] = useState<GuardianStatus | ''>('');
  const [formOpen, setFormOpen] = useState(false);

  const params = { ...table.params, ...(statusFilter !== '' ? { status: statusFilter } : {}) };

  const query = useQuery({
    queryKey: queryKeys.guardians.list(params),
    queryFn: () => guardiansApi.list(params),
  });

  const create = useMutation({
    mutationFn: guardiansApi.create,
    onSuccess: (guardian) => {
      toast.success(`${guardian.fullName} added`);
      setFormOpen(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.guardians.all });
    },
  });

  const columns: Column<Guardian>[] = [
    {
      key: 'name',
      header: 'Name',
      sortKey: 'lastName',
      render: (guardian) => <span className="font-medium text-ink">{guardian.fullName}</span>,
    },
    {
      key: 'phone',
      header: 'Phone',
      sortKey: 'phone',
      render: (guardian) => <span className="font-mono text-xs">{guardian.phone}</span>,
    },
    { key: 'email', header: 'E-mail', render: (guardian) => orDash(guardian.email) },
    { key: 'occupation', header: 'Occupation', render: (guardian) => orDash(guardian.occupation) },
    {
      key: 'children',
      header: 'Children',
      render: (guardian) =>
        guardian.linkedStudentCount === undefined || guardian.linkedStudentCount === 0 ? (
          <span className="text-subtle">Not linked</span>
        ) : (
          <span>
            {guardian.linkedStudentCount}
            <span className="ml-1 text-xs text-subtle">
              {guardian.linkedStudentCount === 1 ? 'child' : 'children'}
            </span>
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (guardian) => <StatusBadge status={guardian.status} />,
    },
  ];

  return (
    <>
      <ListPage
        title="Guardians"
        description="Parents and guardians. One record serves every child they are responsible for, so an edit reaches all of them at once."
        action={
          can(PERMISSIONS.GUARDIAN_CREATE) ? (
            <Button onClick={() => setFormOpen(true)}>Add guardian</Button>
          ) : undefined
        }
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Search by name, phone or e-mail…',
        }}
        filters={
          <FilterSelect
            label="Status"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              table.setPage(0);
            }}
            options={[
              { value: 'ACTIVE', label: 'Active' },
              { value: 'INACTIVE', label: 'Inactive' },
            ]}
          />
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(guardian) => guardian.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          onRowClick={(guardian) => navigate(ROUTES.guardianDetail(guardian.id))}
          emptyTitle="No guardians found"
          emptyDescription={
            table.searchInput === '' && statusFilter === ''
              ? 'Add a guardian, then link them to their children.'
              : 'No guardians match the current filters.'
          }
        />
      </ListPage>

      <GuardianFormModal
        open={formOpen}
        guardian={null}
        saving={create.isPending}
        onClose={() => setFormOpen(false)}
        onSubmit={(values, setError) =>
          create.mutate(toPayload(values), { onError: (error) => handleApiError(error, setError) })
        }
      />
    </>
  );
}
